<template>
  <v-card class="upload-log-viewer">
    <v-card-title class="dialog-header">
      <div class="d-flex align-center flex-grow-1">
        <v-avatar color="#6ec39b" size="40" class="dialog-header-avatar">
          <v-icon color="white" size="22">mdi-file-upload-outline</v-icon>
        </v-avatar>
        <div class="ml-4">
          <h2 class="dialog-header-title">ผลการอัปโหลด CSV</h2>
        </div>
      </div>
      <v-spacer></v-spacer>
      <v-btn icon @click="$emit('close')" class="close-btn">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-card-title>

    <v-divider style="border-color: #e8f5f0; border-width: 2px;"></v-divider>

    <v-card-text v-if="log">
      <!-- ชื่อไฟล์ -->
      <v-row class="mb-3" v-if="log.fileInfo && log.fileInfo.name">
        <v-col cols="12">
          <!-- Template 2: แสดงแยก 2 ไฟล์ -->
          <div v-if="log.templateType === 'template-2' && log.fileInfo.headFileName">
            <div class="d-flex align-center mb-2">
              <v-icon color="primary" class="mr-2">mdi-file-document-outline</v-icon>
              <div>
                <div class="text-subtitle-1 font-weight-medium">ไฟล์ส่วนหัว: {{ log.fileInfo.headFileName }}</div>
                <div class="text-caption text--secondary">
                  {{ formatFileSize(log.fileInfo.headSize) }}
                </div>
              </div>
            </div>
            <div class="d-flex align-center">
              <v-icon color="primary" class="mr-2">mdi-file-table-outline</v-icon>
              <div>
                <div class="text-subtitle-1 font-weight-medium">ไฟล์ส่วนเนื้อใน: {{ log.fileInfo.lineFileName }}</div>
                <div class="text-caption text--secondary">
                  {{ formatFileSize(log.fileInfo.lineSize) }}
                </div>
              </div>
            </div>
            <div class="text-caption text--secondary mt-2">
              รวมทั้งหมด: {{ log.fileInfo.sizeFormatted }}
              <span v-if="log.fileInfo.encoding"> • {{ log.fileInfo.encoding }}</span>
            </div>
          </div>

          <!-- Template 1: แสดงไฟล์เดียว -->
          <div v-else class="d-flex align-center">
            <v-icon color="primary" class="mr-2">mdi-file-document-outline</v-icon>
            <div>
              <div class="text-subtitle-1 font-weight-medium">{{ log.fileInfo.name }}</div>
              <div class="text-caption text--secondary">
                {{ log.fileInfo.sizeFormatted }}
                <span v-if="log.fileInfo.encoding">• {{ log.fileInfo.encoding }}</span>
              </div>
            </div>
          </div>
        </v-col>
      </v-row>

      <!-- สรุปผลการอัปโหลด -->
      <v-row class="mb-4">
        <v-col cols="12">
          <v-alert
            :type="log.status === 'completed' ? 'success' : log.status === 'completed_with_errors' ? 'warning' : 'error'"
            prominent
            dense
          >
            <v-row align="center">
              <v-col class="grow">
                <div class="text-h6">
                  {{ getStatusText(log.status) }}
                </div>
                <div v-if="log.duration">
                  ใช้เวลา: {{ log.duration.toFixed(2) }} วินาที
                </div>
              </v-col>
            </v-row>
          </v-alert>
        </v-col>
      </v-row>

      <!-- สถิติ -->
      <v-row class="mb-4">
        <v-col cols="12" md="4">
          <v-card class="stat-card-detail stat-success" elevation="2">
            <v-card-text class="pa-4">
              <div class="d-flex align-center justify-space-between">
                <div>
                  <div class="stat-label-detail success--text mb-2">
                    <v-icon small color="success" class="mr-1">mdi-check-circle</v-icon>
                    สำเร็จ
                  </div>
                  <div class="stat-number-detail success--text">{{ log.successCount }}</div>
                  <div class="stat-sublabel-detail">รายการ</div>
                </div>
                <v-avatar color="#e8f5f0" size="48">
                  <v-icon color="#6ec39b" size="28">mdi-check-bold</v-icon>
                </v-avatar>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" md="4">
          <v-card class="stat-card-detail stat-error" elevation="2">
            <v-card-text class="pa-4">
              <div class="d-flex align-center justify-space-between">
                <div>
                  <div class="stat-label-detail error--text mb-2">
                    <v-icon small color="error" class="mr-1">mdi-close-circle</v-icon>
                    ไม่สำเร็จ
                  </div>
                  <div class="stat-number-detail error--text">{{ log.failureCount }}</div>
                  <div class="stat-sublabel-detail">รายการ</div>
                </div>
                <v-avatar color="#ffebee" size="48">
                  <v-icon color="#c9242b" size="28">mdi-close-thick</v-icon>
                </v-avatar>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" md="4">
          <v-card class="stat-card-detail stat-total" elevation="2">
            <v-card-text class="pa-4">
              <div class="d-flex align-center justify-space-between">
                <div>
                  <div class="stat-label-detail mb-2" style="color: #707070;">
                    <v-icon small color="#707070" class="mr-1">mdi-file-multiple</v-icon>
                    ทั้งหมด
                  </div>
                  <div class="stat-number-detail" style="color: #707070;">{{ log.totalRecords }}</div>
                  <div class="stat-sublabel-detail">รายการ</div>
                </div>
                <v-avatar color="#f5f5f5" size="48">
                  <v-icon color="#707070" size="28">mdi-cloud-upload</v-icon>
                </v-avatar>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- ข้อมูลไฟล์ -->
      <v-expansion-panels class="mb-4">
        <v-expansion-panel>
          <v-expansion-panel-header>
            <div>
              <v-icon small class="mr-2">mdi-file-document-outline</v-icon>
              ข้อมูลไฟล์
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-simple-table dense>
              <tbody>
                <!-- Template 2: แสดงแยก 2 ไฟล์ -->
                <template v-if="log.templateType === 'template-2' && log.fileInfo?.headFileName">
                  <tr>
                    <td class="font-weight-bold" style="width: 150px">ไฟล์ส่วนหัว:</td>
                    <td>
                      <v-icon x-small class="mr-1">mdi-file-document-outline</v-icon>
                      {{ log.fileInfo.headFileName }}
                      <span class="ml-2 text-caption">({{ formatFileSize(log.fileInfo.headSize) }})</span>
                    </td>
                  </tr>
                  <tr>
                    <td class="font-weight-bold">ไฟล์ส่วนเนื้อใน:</td>
                    <td>
                      <v-icon x-small class="mr-1">mdi-file-table-outline</v-icon>
                      {{ log.fileInfo.lineFileName }}
                      <span class="ml-2 text-caption">({{ formatFileSize(log.fileInfo.lineSize) }})</span>
                    </td>
                  </tr>
                  <tr>
                    <td class="font-weight-bold">ขนาดรวม:</td>
                    <td>
                      <span :class="getFileSizeClass(log.fileInfo?.size)">
                        {{ log.fileInfo?.sizeFormatted || 'N/A' }}
                      </span>
                    </td>
                  </tr>
                </template>

                <!-- Template 1: แสดงไฟล์เดียว -->
                <template v-else>
                  <tr>
                    <td class="font-weight-bold" style="width: 150px">ชื่อไฟล์:</td>
                    <td>{{ log.fileInfo?.name || 'N/A' }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-bold">ขนาด:</td>
                    <td>
                      <span :class="getFileSizeClass(log.fileInfo?.size)">
                        {{ log.fileInfo?.sizeFormatted || 'N/A' }}
                      </span>
                      <span v-if="log.fileInfo?.size === 0" class="ml-2 error--text">
                        (ไฟล์ว่างเปล่า)
                      </span>
                    </td>
                  </tr>
                </template>
                <tr v-if="log.fileInfo?.encoding">
                  <td class="font-weight-bold">Encoding:</td>
                  <td>
                    <v-chip x-small :color="getEncodingColor(log.fileInfo.encoding)" text-color="white">
                      {{ log.fileInfo.encoding }}
                    </v-chip>
                    <span v-if="log.fileInfo.originalEncoding && log.fileInfo.originalEncoding !== 'UTF-8'" class="ml-2 text-caption">
                      (แปลงจาก {{ log.fileInfo.originalEncoding }})
                    </span>
                  </td>
                </tr>
                <tr>
                  <td class="font-weight-bold">Template:</td>
                  <td>
                    <v-chip x-small :color="log.templateType === 'template-1' ? 'blue' : 'purple'" text-color="white">
                      {{ log.templateType === 'template-1' ? 'Template 1' : 'Template 2' }}
                    </v-chip>
                  </td>
                </tr>
                <tr>
                  <td class="font-weight-bold">อัปโหลดโดย:</td>
                  <td>
                    <v-icon small class="mr-1">mdi-account</v-icon>
                    {{ log.uploadedBy || 'N/A' }}
                  </td>
                </tr>
                <tr v-if="log.uploadedAt">
                  <td class="font-weight-bold">เวลา:</td>
                  <td>{{ formatDateTime(log.uploadedAt) }}</td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>

      <!-- รายการที่สำเร็จ -->
      <v-expansion-panels v-if="log.successRecords && log.successRecords.length > 0" class="mb-4">
        <v-expansion-panel>
          <v-expansion-panel-header>
            <div>
              <v-icon small class="mr-2" color="success">mdi-check-circle</v-icon>
              <span class="success--text">รายการที่อัปโหลดสำเร็จ ({{ log.successRecords.length }})</span>
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <!-- แสดงข้อมูลไฟล์สำหรับ Template 2 -->
            <v-chip v-if="log.templateType === 'template-2' && log.fileInfo?.headFileName" x-small color="success" text-color="white" class="mb-3">
              <v-icon x-small left>mdi-file-multiple</v-icon>
              2 ไฟล์: {{ log.fileInfo.headFileName }} + {{ log.fileInfo.lineFileName }}
            </v-chip>
            <v-data-table
              :headers="successHeaders"
              :items="log.successRecords"
              :items-per-page="10"
              :footer-props="{
                'items-per-page-options': [5, 10, 20, 50]
              }"
              class="elevation-1 larger-text-table"
            >
              <template v-slot:[`item.row`]="{ item }">
                <v-chip small color="success" text-color="white" class="text-center">
                  แถว {{ item.row }}
                </v-chip>
              </template>
              <template v-slot:[`item.docID`]="{ item }">
                <span class="font-weight-bold">{{ item.docID }}</span>
              </template>
            </v-data-table>

            <!-- Export ปุ่ม -->
            <div class="mt-4">
              <v-btn
                small
                outlined
                color="success"
                @click="exportSuccessRecords"
              >
                <v-icon small class="mr-2">mdi-download</v-icon>
                ดาวน์โหลดรายการที่สำเร็จ
              </v-btn>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>

      <!-- รายการที่ล้มเหลว -->
      <v-expansion-panels v-if="log.failedRecords && log.failedRecords.length > 0">
        <v-expansion-panel>
          <v-expansion-panel-header>
            <div>
              <v-icon small class="mr-2" color="error">mdi-alert-circle</v-icon>
              <span class="error--text">รายการที่อัปโหลดไม่สำเร็จ ({{ log.failedRecords.length }})</span>
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <!-- แสดงข้อมูลไฟล์สำหรับ Template 2 -->
            <div v-if="log.templateType === 'template-2' && log.fileInfo?.headFileName" class="mb-3">
              <v-chip x-small color="orange" text-color="white">
                <v-icon x-small left>mdi-file-multiple</v-icon>
                2 ไฟล์: {{ log.fileInfo.headFileName }} + {{ log.fileInfo.lineFileName }}
              </v-chip>
              <div class="text-caption grey--text mt-1">
                <v-icon x-small>mdi-information-outline</v-icon>
                ตรวจสอบทั้ง 2 ไฟล์เพื่อแก้ไข error
              </div>
            </div>
            <v-data-table
              :headers="failedHeaders"
              :items="log.failedRecords"
              :items-per-page="10"
              :footer-props="{
                'items-per-page-options': [5, 10, 20, 50]
              }"
              class="elevation-1 larger-text-table"
            >
              <template v-slot:[`item.row`]="{ item }">
                <v-chip small color="error" text-color="white" class="text-center">
                  แถว {{ item.row }}
                </v-chip>
              </template>
              <template v-slot:[`item.docID`]="{ item }">
                <span class="font-weight-bold">{{ item.docID }}</span>
              </template>
              <template v-slot:[`item.error`]="{ item }">
                <v-tooltip bottom max-width="400">
                  <template v-slot:activator="{ on, attrs }">
                    <span
                      v-bind="attrs"
                      v-on="on"
                      class="d-inline-block text-truncate"
                      style="max-width: 300px; text-align: center;"
                    >
                      {{ item.error }}
                    </span>
                  </template>
                  <span>{{ item.error }}</span>
                </v-tooltip>
              </template>
            </v-data-table>

            <!-- Export ปุ่ม -->
            <div class="mt-4">
              <v-btn
                small
                outlined
                color="primary"
                @click="exportFailedRecords"
              >
                <v-icon small class="mr-2">mdi-download</v-icon>
                ดาวน์โหลดรายการที่ล้มเหลว
              </v-btn>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-card-text>

    <v-card-text v-else>
      <v-alert type="info" text>
        ไม่มีข้อมูลการอัปโหลด
      </v-alert>
    </v-card-text>

    <v-divider></v-divider>

    <v-card-actions class="dialog-actions pa-4">
      <v-chip v-if="autoCloseCountdown > 0" small color="#e8f5f0" text-color="#6ec39b">
        <v-icon small class="mr-1">mdi-timer-sand</v-icon>
        ปิดอัตโนมัติใน {{ autoCloseCountdown }} วินาที
      </v-chip>
      <v-spacer></v-spacer>
      <v-btn
        depressed
        color="#6ec39b"
        dark
        @click="$emit('close')"
        class="px-6"
      >
        <v-icon small class="mr-2">mdi-check</v-icon>
        เสร็จสิ้น
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
export default {
  name: 'UploadLogViewer',
  props: {
    log: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      successHeaders: [
        { text: 'แถวที่', value: 'row', width: '100px' },
        { text: 'เลขที่เอกสาร', value: 'docID', width: '300px', align: 'center' },
      ],
      failedHeaders: [
        { text: 'แถวที่', value: 'row', width: '100px' },
        { text: 'เลขที่เอกสาร', value: 'docID', width: '200px', align: 'center' },
        { text: 'สาเหตุ', value: 'error', width: '400px' },
      ],
      autoCloseCountdown: 0,
      autoCloseTimer: null,
    };
  },
  mounted() {
    // เริ่ม countdown 10 วินาทีเพื่อปิด dialog อัตโนมัติ
    if (this.log && this.log.status !== 'processing') {
      this.startAutoCloseTimer();
    }
  },
  beforeDestroy() {
    // ล้าง timer เมื่อ component ถูกทำลาย
    if (this.autoCloseTimer) {
      clearInterval(this.autoCloseTimer);
    }
  },
  methods: {
    startAutoCloseTimer() {
      this.autoCloseCountdown = 10;
      
      this.autoCloseTimer = setInterval(() => {
        this.autoCloseCountdown--;
        
        if (this.autoCloseCountdown <= 0) {
          clearInterval(this.autoCloseTimer);
          this.$emit('close');
        }
      }, 1000);
    },
    
    getStatusText(status) {
      const statusMap = {
        'processing': '⏳ กำลังประมวลผล...',
        'completed': '✅ อัปโหลดสำเร็จ',
        'completed_with_errors': '⚠️ อัปโหลดสำเร็จ แต่มีบางรายการล้มเหลว',
        'failed': '❌ อัปโหลดล้มเหลว',
      };
      return statusMap[status] || status;
    },

    formatFileSize(bytes) {
      if (!bytes || bytes === 0) return '0 KB';
      const kb = bytes / 1024;
      if (kb < 1024) {
        return kb.toFixed(2) + ' KB';
      }
      const mb = kb / 1024;
      return mb.toFixed(2) + ' MB';
    },
    
    getFileSizeClass(size) {
      if (size === 0) return 'error--text font-weight-bold';
      if (size > 10 * 1024 * 1024) return 'warning--text'; // > 10MB
      return '';
    },
    
    getEncodingColor(encoding) {
      if (!encoding) return 'grey';
      if (encoding.includes('UTF-8') || encoding === 'UTF-8') return 'success';
      if (encoding.includes('Windows-1252') || encoding.includes('ANSI')) return 'orange';
      if (encoding.includes('TIS-620')) return 'blue';
      return 'grey';
    },
    
    formatDateTime(timestamp) {
      if (!timestamp) return 'เมื่อสักครู่';
      
      try {
        let date;
        if (timestamp.toDate) {
          // Firestore Timestamp
          date = timestamp.toDate();
        } else if (timestamp instanceof Date) {
          date = timestamp;
        } else if (typeof timestamp === 'number') {
          // Unix timestamp
          date = new Date(timestamp);
        } else {
          date = new Date(timestamp);
        }
        
        // Check if date is valid
        if (isNaN(date.getTime())) {
          return 'เมื่อสักครู่';
        }
        
        return date.toLocaleString('th-TH', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        });
      } catch (error) {
        console.error('Error formatting date:', error);
        return 'เมื่อสักครู่';
      }
    },
    
    exportSuccessRecords() {
      if (!this.log || !this.log.successRecords || this.log.successRecords.length === 0) {
        return;
      }

      // สร้าง CSV content
      const headers = ['แถวที่', 'เลขที่เอกสาร'];
      const rows = this.log.successRecords.map(record => [
        record.row,
        record.docID
      ]);

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');

      // สร้าง Blob และดาวน์โหลด
      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      link.setAttribute('href', url);
      link.setAttribute('download', `success_records_${new Date().getTime()}.csv`);
      link.style.visibility = 'hidden';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    exportFailedRecords() {
      if (!this.log || !this.log.failedRecords || this.log.failedRecords.length === 0) {
        return;
      }

      // สร้าง CSV content
      const headers = ['แถวที่', 'เลขที่เอกสาร', 'สาเหตุ'];
      const rows = this.log.failedRecords.map(record => [
        record.row,
        record.docID,
        record.error
      ]);

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');

      // สร้าง Blob และดาวน์โหลด
      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      link.setAttribute('href', url);
      link.setAttribute('download', `failed_records_${new Date().getTime()}.csv`);
      link.style.visibility = 'hidden';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
  },
};
</script>

<style scoped>
.upload-log-viewer {
  max-height: 80vh;
  overflow-y: auto;
  border-radius: 12px !important;
}

/* Dialog Header */
.dialog-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%) !important;
  padding: 20px 24px !important;
  border-bottom: 2px solid #e8f5f0;
}

.dialog-header-avatar {
  box-shadow: 0 2px 8px rgba(110, 195, 155, 0.3);
  transition: all 0.3s ease;
}

.dialog-header-title {
  font-size: 1.25rem !important;
  font-weight: 700 !important;
  color: #2c3e50 !important;
  line-height: 1.3 !important;
  letter-spacing: -0.01em !important;
  margin: 0 !important;
}

.close-btn {
  transition: all 0.3s ease;
}

.close-btn:hover {
  background-color: rgba(201, 36, 43, 0.1) !important;
  transform: rotate(90deg);
}

/* Stat Cards Detail */
.stat-card-detail {
  border-radius: 12px !important;
  transition: all 0.3s ease;
  border: 1px solid #e0e0e0;
  overflow: hidden;
  position: relative;
  background: white;
}

.stat-card-detail:hover {
  transform: translateY(-4px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15) !important;
}

.stat-success {
  border-left: 4px solid #6ec39b !important;
}

.stat-error {
  border-left: 4px solid #c9242b !important;
}

.stat-total {
  border-left: 4px solid #707070 !important;
}

.stat-label-detail {
  font-size: 0.875rem !important;
  font-weight: 600 !important;
  display: flex;
  align-items: center;
}

.stat-number-detail {
  font-size: 2.5rem !important;
  font-weight: 800 !important;
  line-height: 1.2 !important;
  margin: 8px 0 !important;
}

.stat-sublabel-detail {
  font-size: 0.75rem !important;
  color: #707070 !important;
  font-weight: 500 !important;
}

/* Tables */
.larger-text-table >>> tbody td,
.larger-text-table >>> thead th {
  font-size: 14px !important;
  padding: 12px 16px !important;
}

.larger-text-table >>> tbody td {
  height: 48px !important;
}

/* จัดเลขที่เอกสารให้อยู่กึ่งกลาง */
.larger-text-table >>> tbody tr td:nth-child(2),
.larger-text-table >>> thead tr th:nth-child(2) {
  text-align: center !important;
}

.larger-text-table >>> tbody tr td:nth-child(2) span,
.larger-text-table >>> thead tr th:nth-child(2) {
  display: block !important;
  text-align: center !important;
  width: 100% !important;
}

/* Expansion Panels */
.upload-log-viewer >>> .v-expansion-panel {
  border-radius: 8px !important;
  margin-bottom: 8px;
  border: 1px solid #e0e0e0 !important;
}

.upload-log-viewer >>> .v-expansion-panel:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.upload-log-viewer >>> .v-expansion-panel-header {
  font-weight: 600 !important;
  padding: 16px 20px !important;
}

/* Dialog Actions */
.dialog-actions {
  background: #fafafa !important;
  border-top: 1px solid #e0e0e0;
}

.dialog-actions .v-btn {
  font-weight: 600 !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
  transition: all 0.3s ease;
}

.dialog-actions .v-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(110, 195, 155, 0.4) !important;
}
</style>

